import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pill, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const DEMO_ACCOUNTS = [
  { label: "Owner - Rahul Kapoor", email: "rahulkapoor.ynr@gmail.com" },
  { label: "Admin - Priya Sharma", email: "admin@pharmatask.demo" },
  { label: "Sales Manager - Anjali Desai", email: "anjali.desai@pharmatask.demo" },
  { label: "Sales Executive - Amit Verma", email: "amit.verma@pharmatask.demo" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(err.friendlyMessage || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <div className="relative hidden w-1/2 overflow-hidden bg-sidebar lg:block">
        <img
          src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Pharmaceutical laboratory"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
              <Pill className="h-6 w-6 text-white" />
            </span>
            <div>
              <div className="font-heading text-xl font-bold text-white">PharmaTask</div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-400">Enterprise</div>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight tracking-tight text-white xl:text-5xl">
              Task accountability for pharmaceutical operations.
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-300">
              Create, assign, track and review work across manufacturing, distribution,
              sales and administration — with full hierarchy control and audit trails.
            </p>
            <div className="mt-8 flex gap-6 text-sm text-slate-400">
              <span className="border-l-2 border-teal-500 pl-3">Hierarchy-based assignment</span>
              <span className="border-l-2 border-teal-500 pl-3">Manager review workflow</span>
              <span className="border-l-2 border-teal-500 pl-3">Complete audit trail</span>
            </div>
          </div>
          <div className="text-xs text-slate-500">Demo environment - sample data</div>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
              <Pill className="h-5 w-5 text-white" />
            </span>
            <span className="font-heading text-xl font-bold text-slate-900">PharmaTask Enterprise</span>
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Sign in</h2>
          <p className="mt-1.5 text-sm text-slate-500">Access your company's task management workspace.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            {error && (
              <div data-testid="login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email" data-testid="login-email-input" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" data-testid="login-password-input" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password" className="h-11"
              />
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={busy}
              className="h-11 w-full bg-primary text-base font-semibold hover:bg-[#1E3A8A]">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Demo accounts</div>
            <div className="space-y-1.5">
              {DEMO_ACCOUNTS.map((a) => (
                <button
                  key={a.email}
                  data-testid={`login-demo-${a.email}`}
                  onClick={() => { setEmail(a.email); setPassword("Pharma@123"); }}
                  className="flex w-full items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-left text-sm transition-colors hover:border-primary/30 hover:bg-slate-50"
                >
                  <span className="font-medium text-slate-700">{a.label}</span>
                  <span className="font-data text-xs text-slate-400">{a.email}</span>
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-400">All demo accounts use password: <span className="font-data">Pharma@123</span></p>
          </div>
        </div>
      </div>
    </div>
  );
}
